/**
 * `DashboardMetrics` and `MetricTrend` for the demo.
 *
 * These two payloads carry most of the demo: the dashboard, the executive
 * summary and the velocity graph all derive their headline numbers from
 * `DashboardMetrics`, and `useVelocityScore` computes the velocity rings and
 * grade from it client-side - so getting this right lights up three pages
 * without any page-level code.
 *
 * Everything is a pure function of the filter state. See `../seed.ts` for why
 * that is non-negotiable.
 */

import type { DashboardMetrics, DashboardTopContributor } from '@/types/dashboard';
import type { MetricSummary, MetricTrend, DataPoint } from '@/types/core';
import {
  BOT_IMPACT,
  resolveContributors,
  resolveProfile,
  type DemoRepoProfile,
} from '../fixture';
import { demoDate, jitter, rngFor, type DemoSeedInput } from '../seed';

export interface DemoMetricsParams {
  days: number;
  repoId?: string | null;
  teamId?: string | null;
  excludeBots: boolean;
}

const seedInput = (params: DemoMetricsParams, route: string): DemoSeedInput => ({
  route,
  days: params.days,
  repoId: params.repoId,
  teamId: params.teamId,
  excludeBots: params.excludeBots,
});

/**
 * Volume over a window, with sub-linear damping.
 *
 * A strictly linear `perDay * days` makes the year view a boring 52x of the
 * week view and implies a team whose output never varies. Damping by
 * `0.97 ^ log2(days/7)` keeps longer windows clearly larger while flattening
 * the curve, which is what real quarter-over-quarter data looks like.
 */
function windowVolume(perDay: number, days: number): number {
  const damping = 0.97 ** Math.log2(Math.max(days, 7) / 7);
  return perDay * days * damping;
}

/**
 * Rates regress toward the mean over longer windows.
 *
 * A 7-day slice of a real org is spiky; a year is not. Pulling long windows
 * toward 50% of the way to a neutral midpoint reproduces that without making
 * the year view look broken.
 */
function regressRate(rate: number, days: number, midpoint: number): number {
  const pull = Math.min(0.45, Math.log2(Math.max(days, 7) / 7) * 0.09);
  return rate + (midpoint - rate) * pull;
}

function summary(
  metricName: string,
  current: number,
  previous: number,
  opts: { decimals?: number; unit?: 'percent' | 'pts' } = {}
): MetricSummary {
  const decimals = opts.decimals ?? 1;
  const round = (n: number) => Number(n.toFixed(decimals));
  const currentValue = round(current);
  const previousValue = round(previous);

  // Percentage-point deltas for bounded 0-100 rates, relative percent for
  // everything else. Reporting 2.1 -> 13.3 as "+533%" is the misreading
  // `change_unit` exists to prevent.
  const changePercent =
    opts.unit === 'pts'
      ? Number((currentValue - previousValue).toFixed(1))
      : previousValue === 0
        ? 0
        : Number((((currentValue - previousValue) / previousValue) * 100).toFixed(1));

  const delta = currentValue - previousValue;
  const threshold = Math.abs(previousValue) * 0.02;
  const trend: MetricSummary['trend'] = Math.abs(delta) <= threshold ? 'stable' : delta > 0 ? 'up' : 'down';

  return {
    metric_name: metricName,
    current_value: currentValue,
    previous_value: previousValue,
    change_percent: changePercent,
    change_unit: opts.unit ?? 'percent',
    trend,
  };
}

/** The scalar values a window produces, before they are wrapped in summaries. */
interface WindowValues {
  prsMerged: number;
  cycleTime: number;
  coding: number;
  waitForReview: number;
  reviewToApproval: number;
  approvalToMerge: number;
  reviewCoverage: number;
  testFailureRate: number;
  avgPrSize: number;
  deployFrequency: number;
  changeFailureRate: number;
  mttr: number;
  mergeWithoutApproval: number;
  reworkRate: number;
  leadTimeToDeploy: number;
}

/**
 * Compute one window's values.
 *
 * `windowOffset` of 1 means "the window immediately before this one", which is
 * how every `previous_value` is produced. Deriving the comparison from the same
 * function rather than inventing it keeps `change_percent` and `trend`
 * internally consistent - a demo where the arrow disagrees with the numbers is
 * worse than no arrow.
 */
function computeWindow(
  params: DemoMetricsParams,
  profile: DemoRepoProfile,
  windowOffset: number
): WindowValues {
  const { days, excludeBots } = params;
  // Seed the organic draw with `excludeBots` pinned, so the bots toggle does
  // NOT reseed the underlying numbers - it only layers the bot contribution on
  // top. Without this pin the toggle redraws every jitter, and a +/-3% swing
  // easily swamps the +4pt review-coverage bonus, so "include bots" could
  // LOWER coverage. The toggle has to move things one way, every time, or it
  // reads as noise rather than a filter.
  const rng = rngFor(
    { ...seedInput(params, 'metrics/window'), excludeBots: true },
    `offset=${windowOffset}`
  );

  // Each window gets its own drift so the previous period is not a clone.
  const drift = jitter(rng, 0.08) * (windowOffset === 0 ? 1 : 1 + (rng() - 0.5) * 0.12);

  const botVolume = excludeBots ? 1 : BOT_IMPACT.prVolumeMultiplier;
  const botSize = excludeBots ? 1 : BOT_IMPACT.avgPrSizeMultiplier;
  const botCycle = excludeBots ? 1 : BOT_IMPACT.cycleTimeMultiplier;
  const botCoverage = excludeBots ? 0 : BOT_IMPACT.reviewCoveragePoints;

  const prsMerged = windowVolume(profile.prsPerDay, days) * drift * botVolume;
  const cycleTime = profile.cycleTimeHours * jitter(rng, 0.1) * drift * botCycle;

  // Phases must sum to cycle time exactly, or the breakdown chart and the
  // headline contradict each other on screen.
  const waitForReview = cycleTime * profile.waitForReviewShare;
  const remaining = cycleTime - waitForReview;
  const coding = remaining * 0.54;
  const reviewToApproval = remaining * 0.31;
  const approvalToMerge = remaining - coding - reviewToApproval;

  const reviewCoverage = Math.min(
    99,
    regressRate(profile.reviewCoverageBase * jitter(rng, 0.03), days, 85) + botCoverage
  );
  const testFailureRate = Math.max(
    0.5,
    regressRate(profile.testFailureRateBase * jitter(rng, 0.12), days, 7)
  );

  return {
    prsMerged,
    cycleTime,
    coding,
    waitForReview,
    reviewToApproval,
    approvalToMerge,
    reviewCoverage,
    testFailureRate,
    avgPrSize: profile.avgPrSize * jitter(rng, 0.08) * botSize,
    // Deployments track merges but are not one-per-PR.
    deployFrequency: (prsMerged / days) * 0.62 * jitter(rng, 0.1),
    changeFailureRate: Math.max(
      0.8,
      regressRate(profile.testFailureRateBase * 0.55 * profile.churnMultiplier * jitter(rng, 0.15), days, 5)
    ),
    mttr: 3.2 * profile.churnMultiplier * jitter(rng, 0.18),
    mergeWithoutApproval: Math.max(0, 100 - reviewCoverage) * jitter(rng, 0.1),
    reworkRate: 4.1 * profile.churnMultiplier * jitter(rng, 0.14),
    leadTimeToDeploy: cycleTime * 1.28 * jitter(rng, 0.09),
  };
}

function topContributors(params: DemoMetricsParams, profile: DemoRepoProfile): DashboardTopContributor[] {
  const people = resolveContributors(params.repoId, params.teamId, params.excludeBots);
  const rng = rngFor(seedInput(params, 'metrics/contributors'), 'loc');
  const totalWeight = people.reduce((sum, d) => sum + d.outputWeight, 0) || 1;

  return people
    .map((dev) => {
      // The scoped repo's top contributor gets their planted dominance, which
      // is what makes the bus-factor finding legible on payments-service.
      const share = dev.outputWeight / totalWeight;
      const boosted =
        profile.contributorIds[0] === dev.id
          ? Math.max(share, profile.topContributorShare)
          : share;
      return {
        developer_id: dev.id,
        external_login: dev.login,
        name: dev.name,
        loc_per_day: Number((boosted * 620 * jitter(rng, 0.1)).toFixed(1)),
        review_load: Math.round(dev.reviewGiveWeight * (params.days / 30) * jitter(rng, 0.12)),
      } satisfies DashboardTopContributor;
    })
    .sort((a, b) => b.loc_per_day - a.loc_per_day)
    .slice(0, 8);
}

export function buildDemoDashboardMetrics(params: DemoMetricsParams): DashboardMetrics {
  const profile = resolveProfile(params.repoId, params.teamId);
  const current = computeWindow(params, profile, 0);
  const previous = computeWindow(params, profile, 1);
  const contributors = resolveContributors(params.repoId, params.teamId, params.excludeBots);

  return {
    organization_id: 'demo-org',
    period: `${params.days}d`,
    cycle_time_hours: summary('cycle_time_hours', current.cycleTime, previous.cycleTime),
    prs_merged: summary('prs_merged', Math.round(current.prsMerged), Math.round(previous.prsMerged), { decimals: 0 }),
    deployment_frequency: summary('deployment_frequency', current.deployFrequency, previous.deployFrequency, { decimals: 2 }),
    avg_pr_size: summary('avg_pr_size', Math.round(current.avgPrSize), Math.round(previous.avgPrSize), { decimals: 0 }),
    test_failure_rate: summary('test_failure_rate', current.testFailureRate, previous.testFailureRate, { unit: 'pts' }),
    change_failure_rate: summary('change_failure_rate', current.changeFailureRate, previous.changeFailureRate, { unit: 'pts' }),
    merge_without_approval_rate: summary('merge_without_approval_rate', current.mergeWithoutApproval, previous.mergeWithoutApproval, { unit: 'pts' }),
    review_coverage: summary('review_coverage', current.reviewCoverage, previous.reviewCoverage, { unit: 'pts' }),
    coding_time_hours: summary('coding_time_hours', current.coding, previous.coding),
    wait_for_review_hours: summary('wait_for_review_hours', current.waitForReview, previous.waitForReview),
    review_to_approval_hours: summary('review_to_approval_hours', current.reviewToApproval, previous.reviewToApproval),
    approval_to_merge_hours: summary('approval_to_merge_hours', current.approvalToMerge, previous.approvalToMerge),
    mttr_hours: summary('mttr_hours', current.mttr, previous.mttr),
    lead_time_to_deploy_hours: summary('lead_time_to_deploy_hours', current.leadTimeToDeploy, previous.leadTimeToDeploy),
    rework_rate: summary('rework_rate', current.reworkRate, previous.reworkRate, { unit: 'pts' }),
    top_contributors: topContributors(params, profile),
    total_contributors: contributors.length,
  };
}

/** Baselines per trend metric: [value, relative spread, direction]. */
const TREND_SHAPES: Record<string, { value: (v: WindowValues, days: number) => number; spread: number; drift: number }> = {
  cycle_time_hours: { value: (v) => v.cycleTime, spread: 0.22, drift: -0.12 },
  prs_merged: { value: (v, days) => v.prsMerged / days, spread: 0.35, drift: 0.08 },
  review_coverage_percent: { value: (v) => v.reviewCoverage, spread: 0.07, drift: 0.05 },
  total_lines_changed: { value: (v, days) => (v.prsMerged / days) * v.avgPrSize, spread: 0.3, drift: 0.04 },
  avg_lines_per_active_dev: { value: (v, days) => (v.prsMerged / days) * v.avgPrSize * 0.16, spread: 0.28, drift: 0.03 },
};

/**
 * Daily series for a metric.
 *
 * Always one point per day. The chart's own granularity control
 * (`lib/trendAggregation.ts`) rolls long windows up to weekly or monthly, so
 * emitting daily points here means the existing dropdown keeps working for
 * every preset instead of the generator second-guessing it.
 */
export function buildDemoMetricTrend(
  metricName: string,
  params: DemoMetricsParams
): MetricTrend {
  const profile = resolveProfile(params.repoId, params.teamId);
  const values = computeWindow(params, profile, 0);
  const shape = TREND_SHAPES[metricName];
  const rng = rngFor(seedInput(params, `metrics/trend/${metricName}`), 'series');

  const base = shape ? shape.value(values, params.days) : 1;
  const spread = shape?.spread ?? 0.2;
  const drift = shape?.drift ?? 0;

  const data_points: DataPoint[] = [];
  for (let i = params.days - 1; i >= 0; i--) {
    const progress = (params.days - 1 - i) / Math.max(params.days - 1, 1);
    // Weekends dip for volume metrics - flat weekday-weekend output is one of
    // the first things that reads as synthetic to an engineering leader.
    const dayOfWeek = (i + 3) % 7;
    const weekend = dayOfWeek === 5 || dayOfWeek === 6;
    const weekendFactor = weekend && spread > 0.25 ? 0.35 : 1;

    const value = base * (1 + drift * progress) * jitter(rng, spread) * weekendFactor;
    data_points.push({ date: demoDate(i), value: Math.max(0, Number(value.toFixed(2))) });
  }

  return { metric_name: metricName, data_points };
}
